import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import apiClient, { handleAPIError } from 'helpers/apiService';
//import { useAuth } from 'providers/AuthProvider'
import Button from 'components/base/Button';
//import AuthSocialButtons from 'components/common/AuthSocialButtons';
import { Link } from 'react-router-dom';

interface WizardFormData {
    accept_terms: boolean;
    email: string;
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    company_name: string;
    //phone: string;    
    //address: string;    
    //country: string;
    //zip: number;  
}

const SignUpForm = ({ layout, isAdmin }: { layout: 'simple' | 'card' | 'split', isAdmin: boolean }) => {

    const { t } = useTranslation();

    const [step, setStep] = useState<number>(0);
    const [errors, setErrors] = useState<{
        email?: string, first_name?: string; last_name?: string; company_name?: string; accept_terms?: string,
        registration_error?: string
    }>({});

    const initState: WizardFormData = {
        accept_terms: false,
        email: "",
        first_name: "",
        last_name: "",
        company_name: "",
        password: "",
        confirm_password: "",

    };

    const [signUpData, setSignUpData] = useState<WizardFormData>(initState);

    // Function to handle form data update from child components
    const updateFormData = (name: string, data: any) => {
        setSignUpData({ ...signUpData, [name]: data });
    };

    // Function to handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        updateFormData(name, value);
    };

    const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        updateFormData(name, checked);
    };

    // Validation function for Form 1
    const validateForm1 = (): boolean => {
        const newErrors: { email?: string } = {};
        let isValid = true;

        if (!signUpData.email) {
            newErrors.email = 'Email is required';
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(signUpData.email)) {
            newErrors.email = 'Email is not valid';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const validateForm2 = (): boolean => {
        const newErrors: { full_name?: string; company_name?: string; accept_terms?: string } = {};
        let isValid = true;

        // Validate first/last name
        if (!signUpData.first_name || signUpData.first_name.trim() === '') {
            newErrors.full_name = 'First name is required';
            isValid = false;
        }
        if (!signUpData.last_name || signUpData.last_name.trim() === '') {
            newErrors.full_name = 'Last name is required';
            isValid = false;
        }

        // Validate company_name
        if (!signUpData.company_name || signUpData.company_name.trim() === '') {
            newErrors.company_name = 'Company name is required';
            isValid = false;
        }

        // Validate accept_terms
        if (!signUpData.accept_terms) {
            newErrors.accept_terms = 'You must accept the terms and conditions';
            isValid = false;
        }

        // Set any errors in the state
        setErrors(newErrors);

        return isValid;
    };
    // Function to handle the "Next" button click
    const handleNext = () => {
        if (validateForm1()) {
            // If form 1 is valid, move to the next step
            setStep(1);
        }
    };



    const handleSubmit = async () => {

        if (validateForm2() == false) {
            return
        }

        const newErrors: { registration_error?: string } = {};

        const formData = new FormData();
        formData.append('signUpData', JSON.stringify(signUpData));
        try {
            const response = await apiClient.post(
                `/auth/register`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            if (response.data.success === true)
                setStep(2);

        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                // Set any errors in the state
                newErrors.registration_error = message;
                setErrors(newErrors);
            } else {
                // Handle non-Axios errors
                console.error('Request failed:', error);
            }
            setStep(3);
        }

    };

    const signInWidget = () => {
        return (<div className="text-center">
            <Link to={`/sign-in`} className="fs-9 fw-bold">
                {t("SignUpForm.sign_in")}
            </Link>
        </div>
        )
    }

    return (
        <>
            {step === 0 && (
                <>
                    <div className="text-center mb-7">
                        <h3 className="text-1000">{t("SignUpForm.sign_up")}</h3>
                        <p className="text-700">{t("SignUpForm.line1")}</p>
                    </div>

                    <Form>
                        <Form.Group className="mb-3 text-start">
                            <Form.Label htmlFor="email">{t("SignUpForm.email_address")}</Form.Label>
                            <Form.Control
                                id="email"
                                name="email"
                                type="email"
                                required
                                placeholder="name@example.com"
                                onChange={handleInputChange}
                                isInvalid={!!errors.email}
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide a valid email.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Button variant="primary" onClick={handleNext} className="w-100 mb-3">
                            {t("SignUpForm.continue")}
                        </Button>

                        {!isAdmin && signInWidget()}
                    </Form>
                </>
            )}

            {step === 1 && (
                <>
                    <div className="text-center mb-7">
                        <h3 className="text-1000">{t("SignUpForm.sign_up")}</h3>
                        <p className="text-700">{t("SignUpForm.line1")}</p>
                    </div>
                    <Form>
                        <Form.Group className="mb-3 text-start">
                            <Form.Label htmlFor="name">First Name</Form.Label>
                            <Form.Control id="first_name" type="text"
                                required
                                name="first_name"
                                placeholder="Your first name"
                                onChange={handleInputChange}
                                isInvalid={!!errors.first_name}
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide your first name.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Form.Label htmlFor="name">Last Name</Form.Label>
                            <Form.Control id="last_name" type="text"
                                required
                                name="last_name"
                                placeholder="Your last name"
                                onChange={handleInputChange}
                                isInvalid={!!errors.last_name}
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide your last name.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Form.Label htmlFor="company_name">Company Name</Form.Label>
                            <Form.Control
                                id="company_name"
                                name="company_name"
                                type="text"
                                required
                                onChange={handleInputChange}
                                isInvalid={!!errors.company_name}
                            />
                            <Form.Control.Feedback type="invalid">
                                Company Name cannot be empty.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group>
                            <Form.Check type="checkbox" className="mb-3">
                                <Form.Check.Input
                                    type="checkbox"
                                    name="accept_terms"
                                    id="accept_terms"
                                    required
                                    onChange={handleCheckChange}
                                    isInvalid={!!errors.accept_terms}
                                />
                                <Form.Check.Label htmlFor="accept_terms" className="fs-9 text-none">
                                    I accept the <Link to="#!">terms </Link>and{' '}
                                    <Link to="#!">privacy policy</Link>
                                </Form.Check.Label>
                            </Form.Check>
                            <Form.Control.Feedback type="invalid">
                                You must accept the terms and conditions.
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Button variant="primary" className="w-100 mb-3" onClick={handleSubmit}>
                            {t("SignUpForm.continue")}
                        </Button>

                        {!isAdmin && signInWidget()}
                    </Form>
                </>
            )}

            {step === 2 && (
                <>
                    <div className="text-center mb-7">
                        <h3 className="text-1000">{t("SignUpForm.sign_up_complete")}</h3>
                        <br />
                        <h4 className="text-700">{t("SignUpForm.sign_up_admin_user")}</h4>
                        <br />
                        <h4 className="text-700">{t("SignUpForm.sign_up_admin_email")}</h4>
                    </div>
                    {!isAdmin && signInWidget()}
                </>
            )}

            {step === 3 && (
                <>
                    <div className="text-center mb-7">
                        <h3 className="text-1000 text-danger">{errors.registration_error}</h3>
                    </div>
                    {!isAdmin && signInWidget()}
                </>
            )}
        </>
    );
};

export default SignUpForm;
